"use client"

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type SystemSettings } from '../services/api'
import { Pencil, Shield, Gift } from 'lucide-react'

function EditableNumber({
  value,
  placeholder,
  onSave,
}: {
  value: number | null
  placeholder: string
  onSave: (v: number | null) => void
}) {
  const [editing, setEditing] = useState(false)
  const [raw, setRaw] = useState(value?.toString() ?? '')

  const commit = () => {
    const parsed = raw.trim() === '' ? null : parseInt(raw, 10)
    if (parsed !== null && (isNaN(parsed) || parsed < 0)) return
    onSave(parsed)
    setEditing(false)
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          autoFocus
          type="number"
          min={0}
          className="input input-bordered input-sm w-32"
          placeholder={placeholder}
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit()
            if (e.key === 'Escape') setEditing(false)
          }}
          onBlur={commit}
        />
      </div>
    )
  }

  return (
    <button
      className="group flex items-center gap-1.5 text-sm hover:text-primary transition-colors"
      onClick={() => { setRaw(value?.toString() ?? ''); setEditing(true) }}
    >
      {value != null
        ? <span className="font-mono font-semibold">{value.toLocaleString()}</span>
        : <span className="opacity-40 italic">{placeholder}</span>}
      <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-60 transition-opacity" />
    </button>
  )
}

export default function Settings() {
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['system-settings'],
    queryFn: api.getSystemSettings,
    staleTime: 30000,
  })

  const mutation = useMutation({
    mutationFn: api.updateSystemSettings,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['system-settings'] }),
  })

  if (isLoading) return (
    <div className="p-8">
      <h1 className="page-title mb-6">Settings</h1>
      <div className="flex justify-center items-center h-64">
        <span className="loading loading-spinner loading-lg" />
      </div>
    </div>
  )

  const save = (patch: Partial<SystemSettings>) =>
    mutation.mutate({ ...data, ...patch })

  return (
    <div className="p-8 max-w-2xl">
      <h1 className="page-title mb-8">Settings</h1>

      {/* Freemium */}
      <div className="card bg-base-200 shadow border border-base-300 mb-6">
        <div className="card-body">
          <div className="flex items-center gap-2 mb-1">
            <Gift className="h-4 w-4 text-primary" />
            <h2 className="font-semibold">Freemium Trial</h2>
          </div>
          <p className="text-sm opacity-60 mb-4">
            New users get unrestricted content access for their first N lifetime responses.
            After that, their subscription entitlements apply. Set to blank to disable.
          </p>
          <div className="flex items-center gap-4">
            <span className="text-sm w-40 opacity-70">Free responses</span>
            <EditableNumber
              value={data?.freemium_response_limit ?? null}
              placeholder="Disabled"
              onSave={(v) => save({ freemium_response_limit: v })}
            />
            {data?.freemium_response_limit != null && (
              <span className="badge badge-primary badge-sm">Active — {data.freemium_response_limit} responses</span>
            )}
          </div>
        </div>
      </div>

      {/* Entitlements info */}
      <div className="card bg-base-200 shadow border border-base-300">
        <div className="card-body">
          <div className="flex items-center gap-2 mb-1">
            <Shield className="h-4 w-4 text-secondary" />
            <h2 className="font-semibold">Entitlements Filter</h2>
          </div>
          <p className="text-sm opacity-60">
            Controlled via GitLab feature flag <code className="bg-base-300 px-1 rounded text-xs">enable-entitlements-filter</code>.
            When enabled, users only see content they subscribe to (unless in the freemium window above).
          </p>
        </div>
      </div>

      <p className="text-xs opacity-40 mt-4">
        Changes take effect on the next chat request — no restart needed.
      </p>
    </div>
  )
}
